import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BookOpenText, CalendarDays, PanelsTopLeft, Users, ShieldCheck, Upload, ClipboardCheck, BookMarked, Inbox, FileDown, Newspaper } from "lucide-react";
import AdminOverview from "@/components/admin/AdminOverview";
import AdminQuestions from "@/components/admin/AdminQuestions";
import AdminDaily from "@/components/admin/AdminDaily";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminImport from "@/components/admin/AdminImport";
import AdminReview from "@/components/admin/AdminReview";
import AdminCommentaries from "@/components/admin/AdminCommentaries";
import AdminMessages from "@/components/admin/AdminMessages";
import AdminExport from "@/components/admin/AdminExport";
import AdminBlog from "@/components/admin/AdminBlog";
import Seo from "@/components/Seo";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, el: AdminOverview },
  { key: "questions", label: "Questions", icon: BookOpenText, el: AdminQuestions },
  { key: "import", label: "Import", icon: Upload, el: AdminImport },
  { key: "review", label: "Review queue", icon: ClipboardCheck, el: AdminReview },
  { key: "daily", label: "Daily Spark", icon: CalendarDays, el: AdminDaily },
  { key: "commentaries", label: "Commentaries & Bibles", icon: BookMarked, el: AdminCommentaries },
  { key: "site", label: "Site content", icon: PanelsTopLeft, el: AdminSettings },
  { key: "users", label: "Users", icon: Users, el: AdminUsers },
  { key: "messages", label: "Messages", icon: Inbox, el: AdminMessages },
  { key: "blog", label: "Blog", icon: Newspaper, el: AdminBlog },
  { key: "export", label: "Export PDF", icon: FileDown, el: AdminExport },
];

export default function Admin() {
  const { user, loading, openAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [resetKey, setResetKey] = useState(0);
  const tabKey = params.get("tab") || "overview";
  const tab = TABS.find((t) => t.key === tabKey) || TABS[0];

  if (loading) {
    return <div className="mx-auto max-w-7xl px-4 py-20"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="admin-gate">
        <ShieldCheck className="mx-auto h-10 w-10 text-gold" />
        <h1 className="mt-4 font-serif text-4xl font-bold text-foreground">Admin only</h1>
        <p className="mx-auto mt-3 max-w-md text-muted-foreground">
          {user ? "Your account doesn't have admin access." : "Sign in with an admin account to manage the site."}
        </p>
        {!user ? (
          <Button data-testid="admin-signin-button" onClick={openAuth} className="mt-8 rounded-full bg-primary px-8 text-primary-foreground">Sign in</Button>
        ) : (
          <Link to="/" className="mt-8 inline-block text-primary hover:underline">Back home</Link>
        )}
      </div>
    );
  }

  const Panel = tab.el;
  const go = (key) => {
    if (key === tab.key) setResetKey((k) => k + 1); // re-clicking the current tab returns to its list view
    setParams(key === "overview" ? {} : { tab: key });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-page">
      <Seo title="Admin" />
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Admin</span>
          <h1 className="mt-1 font-serif text-3xl font-bold text-foreground sm:text-4xl">{tab.label}</h1>
        </div>
        <span className="font-mono text-xs text-muted-foreground">{user.email}</span>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <nav className="-mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:w-56 lg:shrink-0 lg:flex-col lg:px-0" data-testid="admin-nav">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => go(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors lg:rounded-xl ${
                t.key === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          <Panel key={`${tab.key}-${resetKey}`} onNavigate={go} />
        </div>
      </div>
    </div>
  );
}
